import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Trash2, Plus } from "lucide-react";
import { toast } from "sonner";

export interface VehiclePricing {
  id?: string;
  vehicle_type_id: string;
  vehicle_type_name?: string;
  price: number;
  currency: string;
}

interface TourVehiclePricingProps {
  vehiclePricing: VehiclePricing[];
  onUpdate: (pricing: VehiclePricing[]) => void;
  vehicleTypes: Array<{ id: string; name_ar: string }>;
}

export const TourVehiclePricing = ({ 
  vehiclePricing, 
  onUpdate, 
  vehicleTypes 
}: TourVehiclePricingProps) => {
  const [newPricing, setNewPricing] = useState<Omit<VehiclePricing, 'id'>>({
    vehicle_type_id: "",
    price: 0,
    currency: "SAR"
  });

  const addVehiclePricing = () => {
    if (!newPricing.vehicle_type_id) {
      toast.error("يرجى اختيار نوع السيارة");
      return;
    }

    // التحقق من عدم وجود نوع السيارة مسبقاً
    const exists = vehiclePricing.some(p => p.vehicle_type_id === newPricing.vehicle_type_id);
    if (exists) {
      toast.error("نوع السيارة موجود مسبقاً");
      return;
    }

    const vehicleType = vehicleTypes.find(v => v.id === newPricing.vehicle_type_id);
    const updatedPricing = [...vehiclePricing, {
      ...newPricing,
      vehicle_type_name: vehicleType?.name_ar
    }];
    
    onUpdate(updatedPricing);
    setNewPricing({
      vehicle_type_id: "",
      price: 0,
      currency: "SAR"
    });
    toast.success("تم إضافة السعر بنجاح");
  };

  const removePricing = (index: number) => {
    const updatedPricing = vehiclePricing.filter((_, i) => i !== index);
    onUpdate(updatedPricing);
    toast.success("تم حذف السعر بنجاح");
  };

  const updatePricing = (index: number, field: keyof VehiclePricing, value: string | number) => {
    const updatedPricing = [...vehiclePricing];
    updatedPricing[index] = { ...updatedPricing[index], [field]: value };
    onUpdate(updatedPricing);
  };

  const availableVehicleTypes = vehicleTypes.filter(
    vt => !vehiclePricing.some(p => p.vehicle_type_id === vt.id)
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>أسعار أنواع السيارات</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* الأسعار الموجودة */}
        {vehiclePricing.map((pricing, index) => {
          const vehicleType = vehicleTypes.find(v => v.id === pricing.vehicle_type_id);
          return (
            <div key={index} className="flex items-center gap-3 p-3 border rounded-lg">
              <Badge variant="outline" className="flex-shrink-0">
                {vehicleType?.name_ar || pricing.vehicle_type_name}
              </Badge>
              
              <div className="flex items-center gap-2 flex-1">
                <Label className="text-sm flex-shrink-0">السعر:</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={pricing.price}
                  onChange={(e) => updatePricing(index, 'price', parseFloat(e.target.value) || 0)}
                  className="w-32"
                />
                
                <Select
                  value={pricing.currency}
                  onValueChange={(value) => updatePricing(index, 'currency', value)}
                >
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SAR">ريال سعودي</SelectItem>
                    <SelectItem value="USD">دولار أمريكي</SelectItem>
                    <SelectItem value="EUR">يورو</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button
                size="sm"
                variant="destructive"
                onClick={() => removePricing(index)}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          );
        })}

        {/* إضافة سعر جديد */}
        {availableVehicleTypes.length > 0 && (
          <div className="border-t pt-4">
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <Label>نوع السيارة</Label>
                <Select
                  value={newPricing.vehicle_type_id}
                  onValueChange={(value) => setNewPricing({ ...newPricing, vehicle_type_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="اختر نوع السيارة" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableVehicleTypes.map(type => (
                      <SelectItem key={type.id} value={type.id}>
                        {type.name_ar}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>السعر</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={newPricing.price}
                  onChange={(e) => setNewPricing({ ...newPricing, price: parseFloat(e.target.value) || 0 })}
                  className="w-32"
                  placeholder="0.00"
                />
              </div>

              <div>
                <Label>العملة</Label>
                <Select
                  value={newPricing.currency}
                  onValueChange={(value) => setNewPricing({ ...newPricing, currency: value })}
                >
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SAR">ريال سعودي</SelectItem>
                    <SelectItem value="USD">دولار أمريكي</SelectItem>
                    <SelectItem value="EUR">يورو</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button onClick={addVehiclePricing}>
                <Plus className="w-4 h-4 mr-2" />
                إضافة
              </Button>
            </div>
          </div>
        )}

        {vehiclePricing.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            لا توجد أسعار محددة للسيارات بعد
          </div>
        )}
      </CardContent>
    </Card>
  );
};